import React, { useState, useEffect, useRef } from 'react';
import PropertyIssuePanel from './PropertyIssuePanel';
import { OnboardingTicketFiling } from './HROnboardingReview';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Shield, 
  Calendar,
  AlertTriangle,
  Award,
  Clock,
  MapPin,
  Phone,
  Mail,
  Building,
  Search,
  Filter,
  Save,
  X,
  Upload,
  Camera,
  User,
  FileText,
  CheckCircle,
  Settings,
  Plus,
  Briefcase,
  Lock,
  Key,
  Eye,
  EyeOff,
  XCircle,
  UserX,
  UserCheck,
  Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import LockoutModal, { lockoutActive, lockoutLabel } from './LockoutModal';
import { KnownAsField } from './KnownAsField';

const canLockout = (u) => ['super_admin', 'office_admin'].includes(u?.role);

// Role → work-area group, for the Shop / Field / Office quick filter.
const ROLE_GROUPS = {
  office: ['super_admin', 'admin', 'hr', 'office_admin', 'project_manager', 'project_manager_estimator', 'estimator', 'dispatch'],
  field: ['millwright_foreman', 'millwright', 'field_employee', 'crew', 'foreman'],
  shop: ['shop_foreman', 'fabrication_manager', 'fabrication', 'welder', 'fitter', 'labourer', 'tool_crib'],
};
const roleInGroup = (role, group) => group === 'all' || (ROLE_GROUPS[group] || []).includes(role);


function EditableShopNumber({ member, onUpdate }) {
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(member.shop_number || '');
  
  // Sync with prop changes
  React.useEffect(() => { setValue(member.shop_number || ''); }, [member.shop_number]);
  
  const save = async () => {
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${member.id}`, { shop_number: value });
      toast.success('Employee # updated');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch { toast.error('Failed to save'); }
  };
  
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input type="text" value={value} onChange={e => setValue(e.target.value)} maxLength="4"
          className="font-mono text-sm font-bold text-blue-600 border border-blue-300 rounded px-2 py-1 w-20 outline-none focus:border-blue-500"
          autoFocus onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }} />
        <button onClick={save} className="text-xs text-blue-600 hover:text-blue-800">Save</button>
      </div>
    );
  }
  return (
    <button onClick={() => setEditing(true)} className="font-mono text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline" title="Click to edit">
      {member.shop_number || '-'}
    </button>
  );
}

const TeamManagement = () => {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [safetyTickets, setSafetyTickets] = useState([]);
  const [scheduleData, setScheduleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [roleGroup, setRoleGroup] = useState('all'); // 'all' | 'office' | 'shop' | 'field'
  const [showInactive, setShowInactive] = useState(false);
  const [sortCol, setSortCol] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  const [editingUser, setEditingUser] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [addStep, setAddStep] = useState('ask'); // 'ask', 'form', 'onboard'
  const [newUserData, setNewUserData] = useState({ full_name: '', email: '', password: '', role: 'field_employee', is_onboarded: true, shop_number: '' });
  const [addingUser, setAddingUser] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [updatingRole, setUpdatingRole] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleRoleChange = async (userId, newRole) => {
    // Prevent multiple simultaneous updates
    if (updatingRole === userId) return;
    
    setUpdatingRole(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        // Update local state
        setTeamMembers(prevMembers =>
          prevMembers.map(member =>
            member.id === userId ? { ...member, role: newRole } : member
          )
        );
        console.log(`✓ Role updated to ${newRole}`);
      } else {
        const error = await response.json();
        console.error('Failed to update role:', error.detail || 'Unknown error');
        // Revert the change in UI by refetching
        fetchTeamMembers();
      }
    } catch (error) {
      console.error('Error updating role:', error);
      // Revert the change in UI by refetching
      fetchTeamMembers();
    } finally {
      setUpdatingRole(null);
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-user-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: resetPasswordUser.id,
          email: resetPasswordUser.email,
          new_password: newPassword
        })
      });

      if (response.ok) {
        toast.success(`Password reset successfully for ${resetPasswordUser.full_name || resetPasswordUser.name}`);
        setShowPasswordReset(false);
        setResetPasswordUser(null);
        setNewPassword('');
        setShowPassword(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('An error occurred. Please try again.');
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const userData = await response.json();
        setCurrentUser(userData);
        
        // Check if user can manage team
        if (['admin', 'super_admin', 'project_manager_estimator', 'office_admin'].includes(userData.role)) {
          await fetchTeamData();
        } else {
          setError('Access denied. Admin or Project Manager privileges required.');
        }
      } else {
        setError('Authentication failed');
      }
    } catch (err) {
      setError('Error verifying authentication');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamData = async () => {
    try {
      await Promise.all([
        fetchTeamMembers(),
        fetchSafetyTickets(),
        fetchScheduleData()
      ]);
    } catch (error) {
      console.error('Error fetching team data:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.users || data || []);
      } else {
        throw new Error(`Failed to fetch team members: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      throw err;
    }
  };

  const fetchSafetyTickets = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/safety-tickets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSafetyTickets(data);
      }
    } catch (err) {
      console.error('Error fetching safety tickets:', err);
    }
  };

  const fetchScheduleData = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/schedule/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setScheduleData(data);
      }
    } catch (err) {
      console.error('Error fetching schedule data:', err);
    }
  };

  const handleUpdateUser = async (userId, userData) => {
    try {
      const response = await fetch(`${backendUrl}/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (response.ok) {
        await fetchTeamMembers();
        setEditingUser(null);
      } else {
        setError('Failed to update user');
      }
    } catch (err) {
      setError('Error updating user');
      console.error('Error updating user:', err);
    }
  };

  const handleDeleteUser = async (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    const name = member?.full_name || 'this user';
    if (!window.confirm(`Mark ${name} inactive?\n\nTheir profile is hidden from the team list, schedule, dropdowns and rosters (tick "Show inactive" to see it) but ALL their hours and work history stay on past projects. You can reactivate them anytime.`)) return;
    try {
      const response = await fetch(`${backendUrl}/api/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(`${name} marked inactive — their hours are preserved`);
        setDeleteConfirmId(null);
        await fetchTeamMembers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to remove user');
      }
    } catch (err) {
      toast.error('Error deleting user');
      console.error('Error deleting user:', err);
    }
  };

  const handleReactivateUser = async (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    const name = member?.full_name || 'this user';
    if (!window.confirm(`Reactivate ${name}?\n\nThey'll be added back to the schedule, dropdowns and active rosters.`)) return;
    try {
      await axios.put(`${backendUrl}/api/users/${userId}`, { is_active: true });
      toast.success(`${name} reactivated`);
      await fetchTeamMembers();
    } catch {
      toast.error('Failed to reactivate user');
    }
  };

  const [lockTarget, setLockTarget] = useState(null);
  const handleLockout = async (payload) => {
    try {
      const r = await axios.post(`${backendUrl}/api/users/${lockTarget.id}/lockout`, payload);
      toast.success(`${lockTarget.full_name} locked out${r.data?.lockout?.until ? ` until ${new Date(r.data.lockout.until).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}` : ''}`);
      setLockTarget(null);
      await fetchTeamMembers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to lock out'); }
  };
  const handleUnlock = async (member) => {
    if (!window.confirm(`Unlock ${member.full_name} now? They'll be able to sign in and clock in again right away.`)) return;
    try {
      await axios.delete(`${backendUrl}/api/users/${member.id}/lockout`);
      toast.success(`${member.full_name} unlocked`);
      await fetchTeamMembers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to unlock'); }
  };

  const handleAddUser = async () => {
    if (!newUserData.full_name || !newUserData.email || !newUserData.password) {
      toast.error('Fill in all required fields');
      return;
    }
    setAddingUser(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/create-user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newUserData),
      });
      if (response.ok) {
        toast.success(`${newUserData.full_name} added`);
        setShowAddUser(false);
        setNewUserData({ full_name: '', email: '', password: '', role: 'field_employee', is_onboarded: true, shop_number: '' });
        await fetchTeamMembers();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to add user');
      }
    } catch (err) {
      toast.error('Error adding user');
    }
    setAddingUser(false);
  };

  // SMS Dispatch System - Phone Number Management
  const handleUpdatePhoneNumber = async (userId, phoneNumber) => {
    console.log('🔧 API call: Updating phone number for user:', userId, 'to:', phoneNumber);
    
    try {
      const response = await fetch(`${backendUrl}/api/users/${userId}/phone`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ phone_number: phoneNumber })
      });

      console.log('🔧 API response status:', response.status);
      
      if (response.ok) {
        console.log('✅ Phone number API call successful');
        await fetchTeamMembers();
        alert('Phone number updated successfully!');
        return true; // Success
      } else {
        const errorData = await response.json();
        console.error('❌ API error response:', errorData);
        alert(`Failed to update phone number: ${errorData.detail || 'Unknown error'}`);
        throw new Error(errorData.detail || 'Unknown error');
      }
    } catch (err) {
      console.error('❌ Network error updating phone number:', err);
      alert('Error updating phone number - check console for details');
      throw err;
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      // Management
      'super_admin': 'bg-purple-50 text-purple-700 border-purple-600',
      'admin': 'bg-red-50 text-red-600 border-red-600',
      'hr': 'bg-pink-50 text-pink-700 border-pink-600',
      'project_manager': 'bg-blue-50 text-blue-700 border-blue-600',
      'estimator': 'bg-indigo-50 text-indigo-700 border-indigo-600',
      
      // Field Leadership
      'millwright_foreman': 'bg-emerald-50 text-emerald-700 border-emerald-600',
      'foreman': 'bg-green-50 text-green-700 border-green-600',
      
      // Field Workers
      'millwright': 'bg-teal-50 text-teal-700 border-teal-600',
      'welder': 'bg-amber-50 text-amber-700 border-amber-600',
      'fitter': 'bg-lime-50 text-lime-700 border-lime-600',
      'labourer': 'bg-slate-200 text-slate-500 border-slate-300',
      
      // Legacy
      'project_manager_estimator': 'bg-blue-50 text-blue-700 border-blue-600',
      'office_admin': 'bg-cyan-50 text-cyan-700 border-cyan-600',
      'field_employee': 'bg-slate-200 text-slate-500 border-slate-300',
      'dispatch': 'bg-orange-50 text-orange-700 border-orange-600',
      'crew': 'bg-slate-200 text-slate-500 border-slate-300',
      'fabrication': 'bg-slate-200 text-slate-500 border-slate-300'
    };
    return colors[role] || 'bg-slate-200 text-slate-500 border-slate-300';
  };

  const getUserSafetyTickets = (userId) => {
    return safetyTickets.filter(ticket => 
      ticket.employee_id === userId || 
      ticket.reported_by === userId
    );
  };

  const getUserSchedule = (userId) => {
    return scheduleData.filter(schedule => 
      schedule.employee_id === userId
    );
  };

  const filteredTeamMembers = teamMembers.filter(member => {
    const matchesSearch = member.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || member.role === selectedRole;
    const matchesGroup = roleInGroup(member.role, roleGroup);
    const matchesActive = showInactive || member.is_active !== false;
    return matchesSearch && matchesRole && matchesGroup && matchesActive;
  }).sort((a, b) => {
    let va, vb;
    if (sortCol === 'last_name') {
      va = (a.full_name || '').split(' ').slice(-1)[0].toLowerCase();
      vb = (b.full_name || '').split(' ').slice(-1)[0].toLowerCase();
    } else if (sortCol === 'name') {
      va = (a.full_name || '').toLowerCase();
      vb = (b.full_name || '').toLowerCase();
    } else if (sortCol === 'email') {
      va = (a.email || '').toLowerCase();
      vb = (b.email || '').toLowerCase();
    } else if (sortCol === 'role') {
      va = (a.role || '').toLowerCase();
      vb = (b.role || '').toLowerCase();
    } else if (sortCol === 'shop_number') {
      va = a.shop_number || 'zzzz';
      vb = b.shop_number || 'zzzz';
    } else if (sortCol === 'status') {
      va = a.is_active === false ? 1 : 0;
      vb = b.is_active === false ? 1 : 0;
    } else {
      va = ''; vb = '';
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-slate-900">Loading team management...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!currentUser || !['admin', 'super_admin', 'project_manager_estimator', 'office_admin'].includes(currentUser.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-red-500">Access denied. Admin or Project Manager privileges required.</div>
      </div>
    );
  }

  const mainContent = (
    <div className="p-6 max-w-full mx-auto bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Team Management</h1>
          <p className="text-slate-600 mt-1">
            Manage team members and safety records
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { setShowAddUser(true); setAddStep('ask'); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-lg"
          >
            <UserPlus className="h-4 w-4" />
            Add Team Member
          </button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-white/60 backdrop-blur-xl border-slate-200 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search team members..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Work-area group filter: Shop / Field / Office */}
            <div className="flex items-center gap-1 bg-white/70 border border-slate-200 rounded-lg p-1" data-testid="role-group-filter">
              {[
                { id: 'all', label: 'All' },
                { id: 'office', label: 'Office' },
                { id: 'shop', label: 'Shop' },
                { id: 'field', label: 'Field' },
              ].map(g => (
                <button key={g.id} onClick={() => setRoleGroup(g.id)}
                  data-testid={`role-group-${g.id}`}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                    roleGroup === g.id ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                  }`}>
                  {g.label}
                </button>
              ))}
            </div>

            {/* Role Filter */}
            <div className="min-w-48">
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Roles</option>
                <optgroup label="Management">
                  <option value="super_admin">Super Admin</option>
                  <option value="office_admin">Office Admin</option>
                  <option value="fabrication_manager">Fabrication Manager</option>
                  <option value="project_manager_estimator">PM/Estimator</option>
                  <option value="dispatch">Dispatch</option>
                </optgroup>
                <optgroup label="Shop">
                  <option value="shop_foreman">Fab Shop Foreman</option>
                  <option value="millwright_foreman">Millwright Foreman</option>
                  <option value="millwright">Millwright</option>
                  <option value="welder">Welder - Fitter</option>
                  <option value="labourer">Labourer</option>
                  <option value="tool_crib">Tool Crib</option>
                </optgroup>
              </select>
            </div>

            {/* Show Inactive toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="show-inactive-toggle">
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)}
                className="rounded border-slate-400 text-blue-600 w-4 h-4" data-testid="show-inactive-checkbox" />
              <span className="text-sm text-slate-700">Show inactive <span className="text-slate-500">({teamMembers.filter(m => m.is_active === false).length})</span></span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs defaultValue="team" className="space-y-4">
        <TabsList className="bg-white border border-slate-200 shadow-sm">
          <TabsTrigger value="team" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-700">
            <Users className="h-4 w-4 mr-2" />
            Team Members ({filteredTeamMembers.length})
          </TabsTrigger>
          <TabsTrigger value="safety" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-700">
            <Shield className="h-4 w-4 mr-2" />
            Safety & Training
          </TabsTrigger>
        </TabsList>

        {/* Team Members Tab */}
        <TabsContent value="team">
          {/* View Mode Toggle */}
          <div className="flex justify-end mb-4">
            <div className="bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                List View
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Grid View
              </button>
            </div>
          </div>

          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      { key: 'shop_number', label: '#' },
                      { key: 'last_name', label: 'Name' },
                      { key: 'role', label: 'Role' },
                      { key: 'status', label: 'Status' },
                    ].map(col => (
                      <th key={col.key}
                        onClick={() => { if (sortCol === col.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col.key); setSortDir('asc'); } }}
                        className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none transition-colors ${sortCol === col.key ? 'text-blue-700 bg-blue-50' : 'text-slate-600 hover:text-gray-700'}`}>
                        <span className="flex items-center gap-1">
                          {col.label}
                          {sortCol === col.key && <span className="text-blue-500">{sortDir === 'asc' ? ' \u25B2' : ' \u25BC'}</span>}
                        </span>
                      </th>
                    ))}
                    <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTeamMembers.map((member, idx) => (
                    <tr key={member.id} className={idx % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-slate-100 hover:bg-blue-50'}>
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                        <EditableShopNumber member={member} onUpdate={fetchTeamMembers} />
                      </td>
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-slate-900 font-semibold text-xs">
                            {member.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                          </div>
                          <div className="ml-2 sm:ml-3">
                            <button onClick={() => setSelectedUserId(member.id)}
                              className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors cursor-pointer text-left">
                              {member.full_name}
                            </button>
                            <p className="text-xs text-gray-500 sm:hidden">{member.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 whitespace-nowrap">
                        {currentUser?.role === 'super_admin' ? (
                          <select value={member.role} onChange={(e) => handleRoleChange(member.id, e.target.value)}
                            disabled={updatingRole === member.id}
                            className="text-xs border border-gray-300 rounded px-2 py-1 cursor-pointer">
                            <optgroup label="Management"><option value="super_admin">Super Admin</option><option value="office_admin">Office Admin</option><option value="fabrication_manager">Fabrication Manager</option><option value="project_manager_estimator">PM/Estimator</option><option value="dispatch">Dispatch</option></optgroup><optgroup label="Shop"><option value="shop_foreman">Fab Shop Foreman</option><option value="millwright_foreman">Millwright Foreman</option><option value="millwright">Millwright</option><option value="welder">Welder - Fitter</option><option value="labourer">Labourer</option><option value="tool_crib">Tool Crib</option></optgroup>
                          </select>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-700">
                            {member.role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex flex-col gap-1 items-start">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            member.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`} data-testid={`status-badge-${member.id}`}>
                            {member.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                          {lockoutActive(member) && (
                            <span className="px-2 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full bg-red-600 text-white" title={member.lockout.reason || ''} data-testid={`lockout-badge-${member.id}`}>
                              <Lock className="h-3 w-3" /> {lockoutLabel(member)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-right text-sm">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setSelectedUserId(member.id)} className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded" title="Edit">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          {canLockout(currentUser) && member.is_active !== false && member.id !== currentUser?.id && (
                            lockoutActive(member) ? (
                              <button onClick={() => handleUnlock(member)} className="p-1.5 text-green-700 hover:bg-green-50 rounded" title={`Unlock now — ${lockoutLabel(member)}`} data-testid={`unlock-user-${member.id}`}>
                                <Unlock className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <button onClick={() => setLockTarget(member)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Lock out temporarily — blocks Ops login + kiosk clock-in, keeps them on the schedule" data-testid={`lockout-user-${member.id}`}>
                                <Lock className="h-3.5 w-3.5" />
                              </button>
                            )
                          )}
                          <button onClick={async () => {
                            const pw = (member.full_name || 'User').split(' ')[0] + '123';
                            if (!window.confirm(`Reset ${member.full_name}'s password to "${pw}" and email it to ${member.email}?`)) return;
                            try {
                              const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-user-password`,
                                { user_id: member.id, email: member.email, new_password: pw, send_email: true });
                              if (r.data?.email_sent) toast.success(`Password reset & emailed to ${r.data.email}`);
                              else toast.error(r.data?.message || 'Password reset but the email did not send');
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
                          }} className="p-1.5 text-amber-600 hover:text-amber-900 hover:bg-amber-50 rounded" title="Reset password & email" data-testid={`reset-email-user-${member.id}`}>
                            <Mail className="h-3.5 w-3.5" />
                          </button>
                          {member.is_active !== false ? (
                            <button onClick={() => handleDeleteUser(member.id)}
                              className="ml-1 px-2 py-1 inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded"
                              title="Mark inactive — hides their profile from the team list, schedule and dropdowns; all their hours stay. Reversible."
                              data-testid={`remove-user-${member.id}`}>
                              <UserX className="h-3.5 w-3.5" /> Mark inactive
                            </button>
                          ) : (
                            <button onClick={() => handleReactivateUser(member.id)}
                              className="ml-1 px-2 py-1 inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded"
                              title="Reactivate — puts them back on the team list, schedule and dropdowns"
                              data-testid={`reactivate-user-${member.id}`}>
                              <UserCheck className="h-3.5 w-3.5" /> Reactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredTeamMembers.map((member) => (
                <TeamMemberCard 
                  key={member.id}
                member={member}
                safetyTickets={getUserSafetyTickets(member.id)}
                schedule={getUserSchedule(member.id)}
                onEdit={setEditingUser}
                onDelete={handleDeleteUser}
                onReactivate={handleReactivateUser}
                onViewDetails={setSelectedUserId}
                onUpdatePhone={handleUpdatePhoneNumber}
                onResetPassword={(m) => { setResetPasswordUser(m); setShowPasswordReset(true); }}
                getRoleBadgeColor={getRoleBadgeColor}
                lockControls={canLockout(currentUser) && member.id !== currentUser?.id ? { onLock: () => setLockTarget(member), onUnlock: () => handleUnlock(member) } : null}
              />
            ))}
            </div>
          )}
        </TabsContent>

        {/* Safety & Training Tab */}
        <TabsContent value="safety">
          <SafetyOverview 
            safetyTickets={safetyTickets}
            teamMembers={teamMembers}
          />
        </TabsContent>
      </Tabs>
      
      {/* Team Member Details Modal */}
      {selectedUserId && (
        <TeamMemberDetailsModal
          userId={selectedUserId}
          teamMembers={teamMembers}
          safetyTickets={safetyTickets}
          scheduleData={scheduleData}
          onClose={() => setSelectedUserId(null)}
          onUpdate={fetchTeamData}
          currentUser={currentUser}
          onLock={(m) => setLockTarget(m)}
          onUnlock={handleUnlock}
        />
      )}

      {lockTarget && <LockoutModal member={lockTarget} onConfirm={handleLockout} onClose={() => setLockTarget(null)} />}

      {/* Password Reset Modal */}
      {showPasswordReset && resetPasswordUser && (
        <Dialog open={showPasswordReset} onOpenChange={setShowPasswordReset}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Reset Password
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-900">Resetting password for:</p>
                <p className="font-bold text-blue-900">{resetPasswordUser.full_name || resetPasswordUser.name}</p>
                <p className="text-sm text-blue-700">{resetPasswordUser.email}</p>
              </div>

              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-600 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-600 mt-1">Minimum 6 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordReset(false);
                  setResetPasswordUser(null);
                  setNewPassword('');
                  setShowPassword(false);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handlePasswordReset}>
                Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );

  return (<>{mainContent}{showAddUser && (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">

        {/* Step 1: Ask if onboarded */}
        {addStep === 'ask' && (
          <>
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-800">Add Team Member</h3>
              <button onClick={() => setShowAddUser(false)} className="text-slate-500 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-700 mb-6">Has this employee been onboarded?</p>
              <div className="space-y-3">
                <button onClick={() => { setAddStep('form'); setNewUserData(d => ({...d, is_onboarded: true})); }}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm">
                  Yes — Add to system
                </button>
                <button onClick={() => navigate('/hr/admin')}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm">
                  No — Start Onboarding
                </button>
                <button onClick={() => setShowAddUser(false)}
                  className="w-full py-2 text-slate-500 hover:text-slate-700 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 2: Add user form (already onboarded) */}
        {addStep === 'form' && (
          <>
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Add Team Member</h3>
          <button onClick={() => setShowAddUser(false)} className="text-slate-500 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
            <input type="text" value={newUserData.full_name} onChange={e => setNewUserData(d => ({ ...d, full_name: e.target.value }))} placeholder="John Smith"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
            <input type="email" value={newUserData.email} onChange={e => setNewUserData(d => ({ ...d, email: e.target.value }))} placeholder="jsmith@365isi.com"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
            <div className="flex gap-2">
              <input type="text" value={newUserData.password} onChange={e => setNewUserData(d => ({ ...d, password: e.target.value }))} placeholder="Firstname123"
                className="flex-1 px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none" />
              <button type="button" onClick={() => { const first = newUserData.full_name.split(' ')[0] || 'User'; setNewUserData(d => ({ ...d, password: first + '123' })); }}
                className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-medium whitespace-nowrap">Auto</button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
            <select value={newUserData.role} onChange={e => setNewUserData(d => ({ ...d, role: e.target.value }))}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none">
              <optgroup label="Management"><option value="super_admin">Super Admin</option><option value="office_admin">Office Admin</option><option value="fabrication_manager">Fabrication Manager</option><option value="project_manager_estimator">PM/Estimator</option><option value="dispatch">Dispatch</option></optgroup><optgroup label="Shop"><option value="shop_foreman">Fab Shop Foreman</option><option value="millwright_foreman">Millwright Foreman</option><option value="millwright">Millwright</option><option value="welder">Welder - Fitter</option><option value="labourer">Labourer</option><option value="tool_crib">Tool Crib</option></optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Employee # (4 digits)</label>
            <input type="text" value={newUserData.shop_number} onChange={e => setNewUserData(d => ({ ...d, shop_number: e.target.value }))} placeholder="0045" maxLength="4"
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-slate-900 focus:ring-2 focus:ring-blue-500 outline-none font-mono" />
          </div>
          <div className="flex items-center justify-between py-2">
            <label className="text-sm font-medium text-slate-700">Onboarded</label>
            <button type="button" onClick={() => setNewUserData(d => ({ ...d, is_onboarded: !d.is_onboarded }))}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${newUserData.is_onboarded ? 'bg-blue-600' : 'bg-slate-300'}`}>
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${newUserData.is_onboarded ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>
        <div className="p-6 border-t border-slate-200 flex gap-3 justify-end">
          <button onClick={() => setShowAddUser(false)} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-medium">Cancel</button>
          <button onClick={handleAddUser} disabled={addingUser} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-lg text-sm font-semibold">{addingUser ? 'Adding...' : 'Add User'}</button>
        </div>
          </>
        )}
      </div>
    </div>
  )}</>);
};

// Team Member Card Component
// Team Member Card Component with Phone Number Management
// Per-user permission grants — module keys must match Sidebar permKeys + backend checks.
// `roles` mirrors the Sidebar role gating so we can show "Included with role".
const GRANTABLE_MODULES = [
  { key: 'shop_foreman_review', label: 'Shop Tracker: Foreman Review + Reports (approve shop hours)', roles: ['super_admin', 'office_admin', 'shop_foreman', 'fabrication_manager'] },
  { key: 'field_review', label: 'Field Tracker: PM Review + Field Crew (edit/approve field hours)', roles: ['super_admin', 'admin', 'project_manager', 'project_manager_estimator'] },
  { key: 'field_tracker', label: 'Field Tracker', roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'dispatch', 'millwright_foreman', 'millwright', 'welder', 'labourer', 'tool_crib'] },
  { key: 'consumables', label: 'Consumables (catalog + add items)', roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager', 'shop_foreman', 'dispatch'] },
  { key: 'quotes', label: 'Quotes', roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager'] },
  { key: 'projects', label: 'Projects', roles: ['super_admin', 'office_admin', 'project_manager_estimator', 'fabrication_manager'] },
  { key: 'purchase_orders', label: 'Purchase Orders (raise + email POs)', roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch', 'fabrication_manager', 'shop_foreman'] },
  { key: 'schedule', label: 'Millwright Schedule: edit (move bars, name crew, dailies) — everyone can view', roles: ['super_admin', 'dispatch'] },
  { key: 'fab_scheduler', label: 'Fabrication Scheduler', roles: ['super_admin', 'admin', 'fabrication_manager', 'shop_foreman', 'project_manager', 'project_manager_estimator', 'estimator', 'office_admin'] },
  { key: 'dispatch_requests', label: 'Dispatch Requests (create)', roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch'] },
  { key: 'safety_tickets', label: 'Employee Certifications', roles: ['super_admin', 'admin', 'office_admin', 'hr', 'fabrication_manager', 'dispatch', 'shop_foreman', 'millwright_foreman', 'foreman', 'project_manager', 'project_manager_estimator', 'estimator'] },
  { key: 'packing_slips', label: 'Packing Slips', roles: ['welder', 'fitter', 'shop_foreman', 'fabrication_manager', 'super_admin', 'office_admin'] },
  { key: 'expenses', label: 'Expenses (create reports)', roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'estimator', 'foreman', 'shop_foreman', 'millwright_foreman', 'fabrication_manager', 'millwright'] },
  { key: 'hr_management', label: 'HR Management', roles: ['super_admin', 'office_admin'] },
  { key: 'crm', label: 'CRM', roles: ['super_admin', 'office_admin', 'project_manager_estimator'] },
  { key: 'tool_crib', label: 'Tool Crib (kits, crib stock, truck boxes)', roles: ['super_admin', 'admin', 'office_admin', 'dispatch', 'shop_foreman', 'tool_crib', 'project_manager', 'project_manager_estimator', 'millwright_foreman', 'foreman'] },
  { key: 'customer_portal', label: 'Customer Portal admin', roles: ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch'] },
];
// Roles that appear in the dispatch crew picker by default (mirrors backend CREW_ROLES)
const DISPATCH_CREW_ROLES = ['field_employee', 'foreman', 'crew', 'millwright', 'welder', 'fitter', 'millwright_foreman', 'apprentice', 'labourer', 'labour'];

const TeamMemberCard = ({ member, safetyTickets, schedule, onEdit, onDelete, onReactivate, onViewDetails, getRoleBadgeColor, onUpdatePhone, onResetPassword, lockControls }) => {
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(member.phone_number || '');
  
  const activeSafetyIssues = safetyTickets.filter(ticket => ticket.status === 'open').length;
  const upcomingSchedule = schedule.filter(s => new Date(s.date) >= new Date()).length;

  const handlePhoneSave = async () => {
    try {
      console.log('🔧 Saving phone number:', phoneNumber, 'for user:', member.id);
      await onUpdatePhone(member.id, phoneNumber);
      setEditingPhone(false);
      console.log('✅ Phone number save completed');
    } catch (error) {
      console.error('❌ Phone number save failed:', error);
      // Keep edit mode open so user can try again
    }
  };

  const handlePhoneCancel = () => {
    setPhoneNumber(member.phone_number || '');
    setEditingPhone(false);
  };

  return (
    <Card className="bg-white/60 backdrop-blur-xl border-slate-200 hover:bg-white/70 transition-colors cursor-pointer" onClick={() => onViewDetails(member.id)}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between" onClick={(e) => e.stopPropagation()}>
          <div className="flex-1">
            <CardTitle className="text-slate-900 text-lg leading-tight">
              {member.full_name}
            </CardTitle>
            <p className="text-slate-600 text-sm mt-1">{member.email}</p>
            {lockoutActive(member) && (
              <span className="mt-1 px-2 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full bg-red-600 text-white" title={member.lockout.reason || ''} data-testid={`card-lockout-badge-${member.id}`}>
                <Lock className="h-3 w-3" /> {lockoutLabel(member)}
              </span>
            )}
            
            {/* Phone Number with Editing for SMS Dispatch */}
            <div className="mt-1">
              {editingPhone ? (
                <div className="flex items-center gap-2">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1234567890"
                    className="text-sm bg-white/80 text-slate-900 px-2 py-1 rounded border border-slate-200 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    onClick={handlePhoneSave}
                    className="p-1 text-green-700 hover:text-green-700"
                    title="Save phone number"
                  >
                    <Save className="h-3 w-3" />
                  </button>
                  <button
                    onClick={handlePhoneCancel}
                    className="p-1 text-red-600 hover:text-red-700"
                    title="Cancel"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1 group">
                  <Phone className="h-3 w-3 text-slate-600" />
                  <span className="text-slate-700 text-sm">
                    {member.phone_number || 'No phone'}
                  </span>
                  <button
                    onClick={() => setEditingPhone(true)}
                    className="p-1 text-slate-600 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit phone number for SMS dispatch"
                  >
                    <Edit className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="secondary" className={getRoleBadgeColor(member.role)}>
                {member.role?.replace('_', ' ')}
              </Badge>
            </div>
          </div>
          <div className="flex gap-1 ml-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(member);
              }}
              className="p-1 text-slate-600 hover:text-blue-600 transition-colors"
              title="Edit user"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onResetPassword) onResetPassword(member);
              }}
              className="p-1 text-slate-600 hover:text-orange-600 transition-colors"
              title="Reset password"
            >
              <Lock className="h-4 w-4" />
            </button>
            {lockControls && member.is_active !== false && (
              lockoutActive(member) ? (
                <button onClick={(e) => { e.stopPropagation(); lockControls.onUnlock(); }} className="p-1 text-green-600 hover:text-green-700 transition-colors" title={`Unlock now — ${lockoutLabel(member)}`} data-testid={`card-unlock-user-${member.id}`}>
                  <Unlock className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={(e) => { e.stopPropagation(); lockControls.onLock(); }} className="p-1 text-slate-600 hover:text-red-600 transition-colors" title="Lock out temporarily" data-testid={`card-lockout-user-${member.id}`}>
                  <Lock className="h-4 w-4 text-red-500" />
                </button>
              )
            )}
            {member.is_active !== false ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(member.id);
                }}
                className="p-1 text-slate-600 hover:text-red-600 transition-colors"
                title="Mark inactive — hides their profile; all their hours stay. Reversible."
                data-testid={`card-remove-user-${member.id}`}
              >
                <UserX className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReactivate(member.id);
                }}
                className="p-1 text-green-600 hover:text-green-700 transition-colors"
                title="Reactivate"
                data-testid={`card-reactivate-user-${member.id}`}
              >
                <UserCheck className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Safety Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-slate-600" />
              <span className="text-slate-500 text-sm">Safety Issues:</span>
            </div>
            <Badge variant={activeSafetyIssues > 0 ? "destructive" : "outline"}>
              {activeSafetyIssues}
            </Badge>
          </div>

          {/* Schedule Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <span className="text-slate-500 text-sm">Upcoming Shifts:</span>
            </div>
            <span className="text-slate-900 font-semibold">
              {upcomingSchedule}
            </span>
          </div>

          {/* View Details Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(member.id);
            }}
            className="w-full mt-3 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors shadow-md"
          >
            View Full Details
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

// Team Member Details Modal Component
// Reusable form field — defined outside component to prevent remount on state change
const ProfileField = ({ label, value, onChange, type = 'text', placeholder = '', options = null, half = false }) => (
  <div className={half ? 'col-span-1' : 'col-span-2'}>
    <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
    {options ? (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : type === 'textarea' ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} placeholder={placeholder}
        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
    ) : (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
    )}
  </div>
);

const ProfileCheckbox = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
    <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} data-testid={testid}
      className="w-4 h-4 rounded border-slate-300 text-blue-600" />
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);

const TeamMemberDetailsModal = ({ userId, teamMembers, safetyTickets, scheduleData, onClose, onUpdate, currentUser, onLock, onUnlock }) => {
  const [member, setMember] = useState(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [managerNotes, setManagerNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');
  const [creds, setCreds] = useState(null);
  const [newPinVal, setNewPinVal] = useState('');
  const [newPwVal, setNewPwVal] = useState('');
  const [showPermDelete, setShowPermDelete] = useState(false);
  const [deleteTypedName, setDeleteTypedName] = useState('');
  const [deleteConfirmCheck, setDeleteConfirmCheck] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [companyTrucks, setCompanyTrucks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/owned-equipment`);
        const list = (r.data || []).filter(e => {
          const t = (e.equipment_type || '').toLowerCase();
          const c = (e.equipment_category || '').toLowerCase();
          const n = (e.equipment_name || '').toLowerCase();
          return t.includes('truck') || c.includes('truck') || n.includes('truck');
        });
        setCompanyTrucks(list);
      } catch { setCompanyTrucks([]); }
    })();
  }, []);

  const handleArchiveMember = async () => {
    if (!window.confirm(`Mark ${member?.full_name} inactive?\n\nTheir profile is hidden from the team list, schedule, dropdowns and rosters (tick "Show inactive" to see it) but ALL their hours and work history stay on past projects. You can reactivate them anytime.`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`);
      toast.success(`${member?.full_name} marked inactive — their hours are preserved`);
      if (onUpdate) onUpdate();
      setMember(prev => ({ ...prev, is_active: false }));
    } catch { toast.error('Failed to remove'); }
  };

  const handleReactivateMember = async () => {
    if (!window.confirm(`Reactivate ${member?.full_name}?`)) return;
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}`, { is_active: true });
      toast.success(`${member?.full_name} reactivated`);
      if (onUpdate) onUpdate();
      setMember(prev => ({ ...prev, is_active: true }));
    } catch { toast.error('Failed to reactivate'); }
  };

  const handlePermanentDelete = async () => {
    setDeleting(true);
    try {
      const r = await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/permanent`);
      toast.success(r.data?.message || 'Permanently deleted');
      setShowPermDelete(false);
      if (onUpdate) onUpdate();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(false); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  const loadNotes = async (empId) => {
    try {
      const r = await axios.get(`${backendUrl}/api/shop/employee-notes/${empId}`);
      setManagerNotes(r.data || []);
    } catch { setManagerNotes([]); }
  };

  const loadCreds = async () => {
    try {
      const r = await axios.get(`${backendUrl}/api/admin/user-credentials/${userId}`);
      setCreds(r.data);
    } catch { setCreds({ shop_pin: '', shop_number: member?.shop_number || '' }); }
  };

  const saveAdminPin = async () => {
    if (!/^\d{4}$/.test(newPinVal)) { toast.error('PIN must be exactly 4 digits'); return; }
    try {
      await axios.post(`${backendUrl}/api/admin/set-user-pin`, { user_id: userId, pin: newPinVal });
      toast.success('Kiosk PIN updated');
      setNewPinVal('');
      loadCreds();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set PIN'); }
  };

  const saveAdminPassword = async () => {
    if ((newPwVal || '').length < 4) { toast.error('Password must be at least 4 characters'); return; }
    try {
      await axios.post(`${backendUrl}/api/admin/set-user-password`, { user_id: userId, new_password: newPwVal });
      toast.success('Password reset');
      setNewPwVal('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
  };

  useEffect(() => {
    const m = teamMembers.find(m => m.id === userId);
    if (m) {
      setMember(m);
      const fd = { ...m };
      // Service-vehicle billing default: foremen are assumed to hold a company truck
      // unless explicitly toggled. Reflect that effective value in the toggle.
      if (fd.has_company_truck === undefined || fd.has_company_truck === null) {
        fd.has_company_truck = m.role === 'millwright_foreman';
      }
      setFormData(fd);
      loadNotes(m.id);
    }
  }, [userId, teamMembers]);

  useEffect(() => {
    if (activeTab === 'access' && userId) loadCreds();
  }, [activeTab, userId]);

  const update = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Equipment checklist → company-property sign-off: ticking a box queues that item for the issue form below
  const EQUIP_TO_PROPERTY = {
    equip_laptop: ['laptop', 'Laptop / tablet'], equip_phone: ['phone', 'Company phone'], equip_key_fob: ['key_fob', 'Key fob'], equip_gate_key: ['keys', 'Gate key'],
    equip_safety_glasses: ['ppe', 'Safety glasses'], equip_lockout_locks: ['ppe', 'Lockout locks'], equip_hardhat: ['ppe', 'Hard hat'], equip_vest: ['ppe', 'Hi-vis vest'], equip_respirator: ['ppe', 'Respirator'],
  };
  const [propertyPrefill, setPropertyPrefill] = useState([]);
  const updateEquip = (field, value) => {
    update(field, value);
    const m = EQUIP_TO_PROPERTY[field];
    if (value && m && !formData[field]) {
      setPropertyPrefill(prev => prev.some(r => r.description === m[1]) ? prev : [...prev, { type: m[0], description: m[1] }]);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${backendUrl}/api/users/${userId}`, formData);
      toast.success('Saved');
      if (onUpdate) onUpdate();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (!member) return null;

  const tabs = [
    { id: 'personal', label: 'Personal' },
    { id: 'employment', label: 'Employment' },
    { id: 'emergency', label: 'Emergency' },
    { id: 'policies', label: 'Policies' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'benefits', label: 'Benefits' },
    { id: 'access', label: 'Access' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'notes', label: 'Notes' },
  ];


  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 sm:p-4">
      {showPermDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => !deleting && setShowPermDelete(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}
            data-testid="permanent-delete-modal">
            <div className="bg-red-600 px-6 py-4 flex items-center gap-3">
              <AlertTriangle className="h-6 w-6 text-white flex-shrink-0" />
              <h3 className="text-lg font-semibold text-white">Danger — Permanent Delete</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-slate-700 mb-2">You are about to <span className="font-semibold text-red-700">permanently erase {member?.full_name}</span> from the system.</p>
              <p className="text-sm text-slate-600 mb-4">This is intended for <span className="font-semibold">test/dev accounts only</span>. It cannot be undone. Real employees with any recorded hours, clock-ins or consumables are blocked automatically — use <span className="font-semibold">Remove</span> instead to keep their history on past projects.</p>

              <label className="flex items-start gap-2 mb-4 cursor-pointer">
                <input type="checkbox" checked={deleteConfirmCheck} onChange={e => setDeleteConfirmCheck(e.target.checked)}
                  data-testid="permanent-delete-confirm-check"
                  className="mt-0.5 w-4 h-4 rounded border-slate-300 text-red-600" />
                <span className="text-sm text-slate-700">I understand this permanently erases this account and cannot be undone.</span>
              </label>

              <label className="text-xs font-medium text-slate-700">Type <span className="font-semibold">{member?.full_name}</span> to confirm</label>
              <input autoFocus value={deleteTypedName} onChange={e => setDeleteTypedName(e.target.value)}
                data-testid="permanent-delete-name-input"
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:outline-none"
                placeholder={member?.full_name} />
              <div className="flex gap-2 justify-end mt-5">
                <button onClick={() => setShowPermDelete(false)} disabled={deleting}
                  className="px-4 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100">Cancel</button>
                <button onClick={handlePermanentDelete}
                  disabled={deleting || !deleteConfirmCheck || (deleteTypedName.trim() !== (member?.full_name || '').trim())}
                  data-testid="permanent-delete-confirm-btn"
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {deleting ? 'Deleting…' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="bg-slate-50 sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-white px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-lg">
              {member.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">{member.full_name}</h2>
              <p className="text-sm text-slate-500">{member.email} {member.shop_number ? `· #${member.shop_number}` : ''}</p>
            </div>
            {/* Quick Actions */}
            <div className="flex items-center gap-1 ml-auto mr-2">
              <button onClick={async () => {
                if ((formData.email || '').trim() && (formData.email || '').trim().toLowerCase() !== (member.email || '').trim().toLowerCase()) {
                  toast.error('Save the new email address first, then send the reset');
                  return;
                }
                const pw = (member.full_name || 'User').split(' ')[0] + '123';
                if (!window.confirm(`Reset password to "${pw}" and email it to ${member.email}?`)) return;
                try {
                  const r = await axios.post(`${backendUrl}/api/admin/reset-user-password`, { user_id: member.id, email: member.email, new_password: pw, send_email: true });
                  if (r.data?.email_sent) toast.success(`Password reset & emailed to ${r.data.email}`);
                  else toast.error(r.data?.message || 'Password reset but the email did not send');
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
              }} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Reset Password & Email" data-testid="member-reset-email-btn">
                <Mail className="h-4 w-4" />
              </button>
              {member.is_active !== false ? (
                <button onClick={handleArchiveMember}
                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                  title="Mark inactive — hides their profile; all their hours stay. Reversible."
                  data-testid="member-remove-btn">
                  <UserX className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={handleReactivateMember}
                  className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                  title="Reactivate"
                  data-testid="member-reactivate-btn">
                  <UserCheck className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => { setDeleteTypedName(''); setDeleteConfirmCheck(false); setShowPermDelete(true); }}
                className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded" title="Permanently delete (test accounts only)"
                data-testid="member-permanent-delete-btn">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Tabs */}
        <div className="bg-white px-2 sm:px-6 border-b border-slate-200 flex gap-0 sm:gap-0.5 overflow-x-auto flex-shrink-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`member-tab-${t.id}`}
              className={`px-2.5 sm:px-4 py-2 text-[10px] sm:text-xs font-medium whitespace-nowrap border-b-2 transition-colors flex-shrink-0 ${
                activeTab === t.id ? 'border-blue-600 text-blue-700 bg-blue-50/50' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {activeTab === 'access' && (
            <div className="space-y-6 max-w-lg" data-testid="access-tab">
              {/* Active / Inactive */}
              <div className={`rounded-lg border p-4 ${member.is_active !== false ? 'bg-white border-slate-200' : 'bg-red-50 border-red-200'}`} data-testid="member-status-card">
                <div className="flex items-center gap-2 mb-1">
                  {member.is_active !== false ? <UserCheck className="h-4 w-4 text-green-600" /> : <UserX className="h-4 w-4 text-red-600" />}
                  <p className="text-sm font-semibold text-slate-800">Profile status: <span className={member.is_active !== false ? 'text-green-700' : 'text-red-700'}>{member.is_active !== false ? 'Active' : 'Inactive'}</span></p>
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  {member.is_active !== false
                    ? 'Inactive profiles are hidden from the team list, schedule, dispatch pickers and rosters. All their hours and history stay on past projects, and you can reactivate them anytime.'
                    : `Hidden from the team list and every picker (tick "Show inactive" on the team list to find them).${member.archived_at ? ` Inactive since ${new Date(member.archived_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.` : ''}`}
                </p>
                {member.is_active !== false ? (
                  <button onClick={handleArchiveMember} data-testid="member-status-deactivate-btn"
                    className="px-4 py-2 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"><UserX className="h-4 w-4" /> Mark inactive</button>
                ) : (
                  <button onClick={handleReactivateMember} data-testid="member-status-reactivate-btn"
                    className="px-4 py-2 inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"><UserCheck className="h-4 w-4" /> Reactivate</button>
                )}
              </div>

              {/* Temporary lockout */}
              {canLockout(currentUser) && member.id !== currentUser?.id && (
                <div className={`rounded-lg border p-4 ${lockoutActive(member) ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200'}`} data-testid="member-lockout-card">
                  <div className="flex items-center gap-2 mb-1">
                    {lockoutActive(member) ? <Lock className="h-4 w-4 text-red-600" /> : <Unlock className="h-4 w-4 text-slate-500" />}
                    <p className="text-sm font-semibold text-slate-800">Ops access: <span className={lockoutActive(member) ? 'text-red-700' : 'text-green-700'}>{lockoutActive(member) ? lockoutLabel(member) : 'Allowed'}</span></p>
                  </div>
                  <p className="text-xs text-slate-600 mb-3">
                    {lockoutActive(member)
                      ? `Locked by ${member.lockout.by || 'admin'} on ${new Date(member.lockout.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}${member.lockout.reason ? ` — ${member.lockout.reason}` : ''}. ${member.lockout.until ? 'Lifts automatically on that date, or unlock now.' : 'Stays locked until you unlock.'}`
                      : 'A temporary lockout blocks Ops sign-in and kiosk clock-in without taking them off the schedule, dropdowns or reports. Lifts itself when the time is up.'}
                  </p>
                  {lockoutActive(member) ? (
                    <button onClick={() => onUnlock(member)} data-testid="member-unlock-btn"
                      className="px-4 py-2 inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"><Unlock className="h-4 w-4" /> Unlock now</button>
                  ) : (
                    <button onClick={() => onLock(member)} disabled={member.is_active === false} data-testid="member-lockout-btn"
                      className="px-4 py-2 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"><Lock className="h-4 w-4" /> Lock out temporarily</button>
                  )}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                Manage this employee's kiosk PIN and login password. Passwords are encrypted and cannot be displayed — you can only reset them.
              </div>

              {/* Kiosk PIN */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Key className="h-4 w-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-800">Kiosk PIN</p>
                </div>
                <p className="text-xs text-slate-500 mb-3">
                  Current PIN: <span className="font-mono font-bold text-slate-900" data-testid="admin-current-pin">{creds ? (creds.shop_pin || '— not set —') : 'Loading…'}</span>
                  {creds && creds.shop_number ? <span className="ml-2 text-slate-500">(Shop #{creds.shop_number})</span> : null}
                </p>
                <div className="flex gap-2">
                  <input type="text" inputMode="numeric" maxLength={4} value={newPinVal}
                    onChange={e => setNewPinVal(e.target.value.replace(/\D/g, ''))}
                    placeholder="New 4-digit PIN" data-testid="admin-new-pin"
                    className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm tracking-widest outline-none focus:border-blue-500" />
                  <button onClick={saveAdminPin} disabled={!newPinVal} data-testid="admin-save-pin-btn"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium">Set PIN</button>
                </div>
              </div>

              {/* Login Password */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4 text-slate-500" />
                  <p className="text-sm font-semibold text-slate-800">Login Password</p>
                </div>
                <p className="text-xs text-slate-500 mb-3">Set a new password and share it with the employee.</p>
                <div className="flex gap-2">
                  <input type="text" value={newPwVal} onChange={e => setNewPwVal(e.target.value)}
                    placeholder="New password" data-testid="admin-new-password"
                    className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500" />
                  <button onClick={saveAdminPassword} disabled={!newPwVal} data-testid="admin-save-password-btn"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium">Reset</button>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'permissions' && (() => {
            const roleDefaultCrew = DISPATCH_CREW_ROLES.includes(formData.role);
            const effectiveCrew = formData.dispatchable === true || (formData.dispatchable == null && roleDefaultCrew);
            const grants = formData.extra_permissions || [];
            const toggleGrant = (key, on) => {
              const cur = new Set(grants);
              if (on) cur.add(key); else cur.delete(key);
              update('extra_permissions', [...cur]);
            };
            return (
              <div className="space-y-5 max-w-lg" data-testid="permissions-tab">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  Grant this person access to modules their <b>{(formData.role || '').replace(/_/g, ' ')}</b> role wouldn't normally include.
                  Granting a module also allows <b>creating entries</b> in it (consumable items, dispatch requests, expenses, work periods…).
                  Changes apply the next time they sign in or refresh. Remember to hit <b>Save</b>.
                </div>

                {/* Dispatch crew eligibility */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={effectiveCrew}
                      onChange={e => update('dispatchable', e.target.checked)}
                      data-testid="perm-dispatchable-checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600" />
                    <span>
                      <span className="text-sm font-semibold text-slate-800 block">Can be selected as crew in dispatch requests</span>
                      <span className="text-xs text-slate-500 block mt-0.5">
                        Role default: {roleDefaultCrew ? 'included in the crew picker' : 'NOT in the crew picker'}.
                        {formData.dispatchable != null && (
                          <>
                            {' '}Currently overridden to <b>{formData.dispatchable ? 'always selectable' : 'never selectable'}</b>.{' '}
                            <button type="button" onClick={(e) => { e.preventDefault(); update('dispatchable', null); }}
                              className="text-blue-600 underline" data-testid="perm-dispatchable-reset">Reset to role default</button>
                          </>
                        )}
                      </span>
                    </span>
                  </label>
                </div>

                {/* Module grants */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <p className="text-sm font-semibold text-slate-800 mb-2">Additional Module Access</p>
                  <div className="divide-y divide-slate-100">
                    {GRANTABLE_MODULES.map(m => {
                      const roleHas = m.roles.includes(formData.role);
                      const granted = grants.includes(m.key);
                      return (
                        <label key={m.key} className={`flex items-center justify-between py-2 ${roleHas ? '' : 'cursor-pointer'}`}>
                          <span className="text-sm text-slate-700">{m.label}</span>
                          {roleHas ? (
                            <span className="text-[11px] font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">Included with role</span>
                          ) : (
                            <input type="checkbox" checked={granted}
                              onChange={e => toggleGrant(m.key, e.target.checked)}
                              data-testid={`perm-module-${m.key}`}
                              className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">Admin Panel access stays locked to Super Admins and can't be granted here.</p>
                </div>
              </div>
            );
          })()}
          {activeTab === 'personal' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ProfileField label="Full Legal Name *" value={formData.full_name} onChange={v => update("full_name", v)} placeholder="John Smith" />
              <ProfileField label="Employee Number" value={formData.shop_number} onChange={v => update("shop_number", v)} placeholder="0045" half />
              <ProfileField label="Date of Birth" value={formData.date_of_birth} onChange={v => update("date_of_birth", v)} type="date" half />
              <ProfileField label="Full Home Address" value={formData.address} onChange={v => update("address", v)} placeholder="123 Main St, City, ON K1A 0B1" />
              <ProfileField label="Phone Number (with area code)" value={formData.phone_number} onChange={v => update("phone_number", v)} placeholder="+1 (548) 328-1711" half />
              <ProfileField label="Email Address" value={formData.email} onChange={v => update("email", v)} type="email" half />
              <ProfileField label="Office Extension" value={formData.extension} onChange={v => update("extension", v)} placeholder="123" half />
              <KnownAsField member={member} />
            </div>
          )}

          {activeTab === 'employment' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ProfileField label="Job Title / Position" value={formData.job_title} onChange={v => update("job_title", v)} placeholder="Millwright" half />
              <ProfileField label="Department / Division" value={formData.department} onChange={v => update("department", v)} placeholder="Fabrication" half
                options={[{value:'',label:'Select...'},{value:'Fabrication',label:'Fabrication'},{value:'Millwright',label:'Millwright'},{value:'Office',label:'Office'},{value:'Field',label:'Field'},{value:'Management',label:'Management'}]} />
              <ProfileField label="Role" value={formData.role} onChange={v => update("role", v)} half
                options={[
                  {value:'',label:'Select role...'},
                  {value:'super_admin',label:'Super Admin'},{value:'office_admin',label:'Office Admin'},
                  {value:'fabrication_manager',label:'Fabrication Manager'},{value:'project_manager_estimator',label:'PM/Estimator'},
                  {value:'dispatch',label:'Dispatch'},
                  {value:'shop_foreman',label:'Fab Shop Foreman'},{value:'millwright_foreman',label:'Millwright Foreman'},
                  {value:'millwright',label:'Millwright'},{value:'welder',label:'Welder - Fitter'},
                  {value:'labourer',label:'Labourer'},{value:'tool_crib',label:'Tool Crib'},
                ]} />
              <ProfileField label="Start Date" value={formData.hire_date} onChange={v => update("hire_date", v)} type="date" half />
              <ProfileField label="Probation End Date" value={formData.probation_end} onChange={v => update("probation_end", v)} type="date" half />
              <ProfileField label="Kiosk PIN (4 digits)" value={formData.shop_pin} onChange={v => update("shop_pin", v)} placeholder="1234" half />
            </div>
          )}

          {activeTab === 'emergency' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ProfileField label="Emergency Contact Name *" value={formData.emergency_contact} onChange={v => update("emergency_contact", v)} placeholder="Jane Smith" />
              <ProfileField label="Relationship" value={formData.emergency_relationship} onChange={v => update("emergency_relationship", v)} placeholder="Spouse" half />
              <ProfileField label="Emergency Phone Number(s)" value={formData.emergency_phone} onChange={v => update("emergency_phone", v)} placeholder="+1 (555) 123-4567" half />
              <ProfileField label="Medical Conditions / Restrictions (confidential)" value={formData.medical_conditions} onChange={v => update("medical_conditions", v)} type="textarea" placeholder="Allergies, medications, physical limitations..." />
            </div>
          )}

          {activeTab === 'policies' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 mb-4">Check each policy the employee has acknowledged:</p>
              <ProfileCheckbox label="Employee Handbook — Received and acknowledged" checked={formData.policy_handbook} onChange={v => update("policy_handbook", v)} />
              <ProfileCheckbox label="Code of Conduct — Read and agreed" checked={formData.policy_code_of_conduct} onChange={v => update("policy_code_of_conduct", v)} />
              <ProfileCheckbox label="Attendance Policy — Reviewed and acknowledged" checked={formData.policy_attendance} onChange={v => update("policy_attendance", v)} />
              <ProfileCheckbox label="Drug & Alcohol Policy — Read and agreed" checked={formData.policy_drug_alcohol} onChange={v => update("policy_drug_alcohol", v)} />
              {userId && <OnboardingTicketFiling userId={userId} hideWhenEmpty title="Safety tickets uploaded during onboarding" />}
            </div>
          )}

          {activeTab === 'equipment' && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Truck Assignment</label>
                <select value={formData.vehicle_assignment || ''} onChange={e => update('vehicle_assignment', e.target.value)}
                  data-testid="vehicle-assignment-select"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                  <option value="">— No truck assigned —</option>
                  {companyTrucks.map(t => <option key={t.id} value={t.equipment_name}>{t.equipment_name}</option>)}
                  {formData.vehicle_assignment && !companyTrucks.some(t => t.equipment_name === formData.vehicle_assignment) && (
                    <option value={formData.vehicle_assignment}>{formData.vehicle_assignment} (current)</option>
                  )}
                </select>
                <p className="text-xs text-slate-500 mt-1">Only company trucks appear here. Manage the fleet under Dispatch → Equipment.</p>
              </div>
              <ProfileCheckbox label="Has a company truck (counts as 1 service vehicle on field timesheets — leave unchecked for crew without a truck, e.g. unlicensed foremen)" checked={formData.has_company_truck} onChange={v => update("has_company_truck", v)} testid="has-company-truck-toggle" />
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Office Equipment</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <ProfileCheckbox label="Key Fob" checked={formData.equip_key_fob} onChange={v => updateEquip("equip_key_fob", v)} testid="equip-equip_key_fob" />
                  <ProfileCheckbox label="Gate Key" checked={formData.equip_gate_key} onChange={v => updateEquip("equip_gate_key", v)} testid="equip-equip_gate_key" />
                  <ProfileCheckbox label="Email Account" checked={formData.email_setup} onChange={v => update("email_setup", v)} />
                  <ProfileCheckbox label="Phone" checked={formData.equip_phone} onChange={v => updateEquip("equip_phone", v)} testid="equip-equip_phone" />
                  <ProfileCheckbox label="Laptop / Tablet" checked={formData.equip_laptop} onChange={v => updateEquip("equip_laptop", v)} testid="equip-equip_laptop" />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Safety Equipment</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <ProfileCheckbox label="Safety Glasses" checked={formData.equip_safety_glasses} onChange={v => updateEquip("equip_safety_glasses", v)} testid="equip-equip_safety_glasses" />
                  <ProfileCheckbox label="Lockout Locks" checked={formData.equip_lockout_locks} onChange={v => updateEquip("equip_lockout_locks", v)} testid="equip-equip_lockout_locks" />
                  <ProfileCheckbox label="Hard Hat" checked={formData.equip_hardhat} onChange={v => updateEquip("equip_hardhat", v)} testid="equip-equip_hardhat" />
                  <ProfileCheckbox label="Vest" checked={formData.equip_vest} onChange={v => updateEquip("equip_vest", v)} testid="equip-equip_vest" />
                  <ProfileCheckbox label="Respirator" checked={formData.equip_respirator} onChange={v => updateEquip("equip_respirator", v)} testid="equip-equip_respirator" />
                </div>
              </div>
              <ProfileField label="Other Equipment Notes" value={formData.keys_access} onChange={v => update("keys_access", v)} type="textarea" placeholder="Any other assigned items..." />
              <p className="text-[11px] text-slate-500 -mt-2">Ticking a box above records it on the profile AND queues it in the sign-off form below — issue it there so the employee gets notified and signs for it.</p>
              <div className="border-t border-slate-200 pt-4">
                <PropertyIssuePanel userId={userId} prefill={propertyPrefill} onPrefillConsumed={() => setPropertyPrefill([])} />
              </div>
            </div>
          )}

          {activeTab === 'benefits' && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600 mb-4">Benefits enrollment status:</p>
              <ProfileCheckbox label="Health / Dental Plan — Enrolled" checked={formData.benefits_health_dental} onChange={v => update("benefits_health_dental", v)} />
              <ProfileCheckbox label="Pension or RRSP — Enrolled" checked={formData.benefits_pension_rrsp} onChange={v => update("benefits_pension_rrsp", v)} />
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Manager Notes Feed */}
              <div>
                <p className="text-sm font-semibold text-slate-700 mb-3">Manager Notes & Incidents</p>
                <div className="flex gap-2 mb-3">
                  <select value={noteCategory} onChange={(e) => setNoteCategory(e.target.value)}
                    className="px-2 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                    <option value="general">General</option>
                    <option value="performance">Performance</option>
                    <option value="discipline">Discipline</option>
                    <option value="safety">Safety</option>
                    <option value="incident">Incident</option>
                    <option value="positive">Positive</option>
                  </select>
                  <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && newNote.trim()) {
                        try {
                          await axios.post(`${backendUrl}/api/shop/employee-notes`, { employee_id: userId, note: newNote, category: noteCategory });
                          toast.success('Note added');
                          setNewNote('');
                          loadNotes(userId);
                        } catch { toast.error('Failed'); }
                      }
                    }} />
                  <button onClick={async () => {
                    if (!newNote.trim()) return;
                    try {
                      await axios.post(`${backendUrl}/api/shop/employee-notes`, { employee_id: userId, note: newNote, category: noteCategory });
                      toast.success('Note added');
                      setNewNote('');
                      loadNotes(userId);
                    } catch { toast.error('Failed'); }
                  }} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                    Add
                  </button>
                </div>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {managerNotes.length === 0 ? (
                    <p className="text-sm text-slate-500 py-4 text-center">No notes yet</p>
                  ) : managerNotes.map(n => (
                    <div key={n.id} className={`p-3 rounded-lg border text-sm ${
                      n.category === 'discipline' ? 'bg-red-50 border-red-200' :
                      n.category === 'incident' ? 'bg-orange-50 border-orange-200' :
                      n.category === 'safety' ? 'bg-yellow-50 border-yellow-200' :
                      n.category === 'positive' ? 'bg-green-50 border-green-200' :
                      n.category === 'performance' ? 'bg-blue-50 border-blue-200' :
                      'bg-slate-50 border-slate-200'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded mr-2 ${
                            n.category === 'discipline' ? 'bg-red-100 text-red-700' :
                            n.category === 'incident' ? 'bg-orange-100 text-orange-700' :
                            n.category === 'safety' ? 'bg-yellow-100 text-yellow-700' :
                            n.category === 'positive' ? 'bg-green-100 text-green-700' :
                            n.category === 'performance' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>{n.category}</span>
                          <p className="text-slate-800 mt-1">{n.note}</p>
                        </div>
                        <button onClick={async () => {
                          try {
                            await axios.delete(`${backendUrl}/api/shop/employee-notes/${n.id}`);
                            loadNotes(userId);
                          } catch {}
                        }} className="text-slate-700 hover:text-red-500 ml-2 text-xs">x</button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{n.added_by} — {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Clothing & Equipment */}
              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-200">
                <div className="col-span-2">
                  <p className="text-sm font-semibold text-slate-700 mb-3">Clothing Sizes (for uniforms/PPE)</p>
                </div>
                <ProfileField label="Shirt Size" value={formData.clothing_shirt} onChange={v => update("clothing_shirt", v)} placeholder="XL" half />
                <ProfileField label="Pants Size" value={formData.clothing_pants} onChange={v => update("clothing_pants", v)} placeholder="34x32" half />
                <ProfileField label="Boot Size" value={formData.clothing_boot} onChange={v => update("clothing_boot", v)} placeholder="10.5" half />
                <ProfileField label="Glove Size" value={formData.clothing_glove} onChange={v => update("clothing_glove", v)} placeholder="L" half />
                <ProfileField label="Hard Hat Size" value={formData.clothing_hardhat} onChange={v => update("clothing_hardhat", v)} placeholder="7 1/4" half />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 flex items-center justify-between flex-shrink-0 gap-2">
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <span className={`px-2 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full ${member.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-testid="member-status-badge">
              {member.is_active !== false ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex gap-2 sm:gap-3">
            <button onClick={onClose} className="px-3 sm:px-4 py-2 text-slate-600 hover:text-slate-800 text-xs sm:text-sm font-medium">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="member-profile-save-btn"
              className="px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white rounded-lg text-xs sm:text-sm font-semibold flex items-center gap-1 sm:gap-2">
              <Save className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Safety Certificates Section Component
const SafetyCertificatesSection = ({ userId, memberSafetyTickets, onUpdate }) => {
  const [certificates, setCertificates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCertificates();
  }, [userId]);

  const fetchCertificates = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/users/${userId}/safety-certificates`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const certs = await response.json();
        setCertificates(certs);
      } else {
        console.error('Failed to fetch certificates, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('certificate_file', file);
        formData.append('certificate_name', file.name);
        formData.append('issuing_organization', 'Unknown');

        const response = await fetch(`${backendUrl}/api/users/${userId}/safety-certificates`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          toast.success(`Certificate ${file.name} uploaded successfully`);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      }

      // Refresh certificates after upload
      await fetchCertificates();
      onUpdate(); // Refresh parent data

    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading certificates');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteCertificate = async (certificateId) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/safety-certificates/${certificateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Certificate deleted successfully');
        await fetchCertificates();
        onUpdate();
      } else {
        toast.error('Failed to delete certificate');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error deleting certificate');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'current': 'bg-green-50 text-green-700 border-green-600',
      'expiring_soon': 'bg-yellow-50 text-amber-700 border-yellow-600',
      'expired': 'bg-red-50 text-red-600 border-red-600',
      'unknown': 'bg-slate-200 text-slate-500 border-slate-300'
    };
    return colors[status] || colors.unknown;
  };

  const getCertificateTypeIcon = (type) => {
    const icons = {
      'welding': '🔥',
      'safety_training': '🦺',
      'equipment_operation': '🏗️',
      'company_specific': '🏢',
      'specialized': '⚡',
      'other': '📋'
    };
    return icons[type] || icons.other;
  };

  const groupedCertificates = certificates.reduce((groups, cert) => {
    const type = cert.certificate_type || 'other';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(cert);
    return groups;
  }, {});

  // Calculate summary stats
  const currentCerts = certificates.filter(c => c.status === 'current').length;
  const expiringSoon = certificates.filter(c => c.status === 'expiring_soon').length;
  const expiredCerts = certificates.filter(c => c.status === 'expired').length;
  const totalCerts = certificates.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-slate-900">Loading safety certificates...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Certificate Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-700" />
              <div>
                <p className="text-sm text-slate-500">Current</p>
                <p className="text-xl font-bold text-green-700">{currentCerts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-8 w-8 text-amber-700" />
              <div>
                <p className="text-sm text-slate-500">Expiring Soon</p>
                <p className="text-xl font-bold text-amber-700">{expiringSoon}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <X className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-sm text-slate-500">Expired</p>
                <p className="text-xl font-bold text-red-600">{expiredCerts}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-blue-700" />
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-xl font-bold text-blue-700">{totalCerts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Safety Certificates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-gray-500 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-900 font-medium mb-2">
                {uploading ? 'Uploading certificates...' : 'Click to upload certificates'}
              </p>
              <p className="text-slate-500 text-sm">
                Support for PDF, JPG, PNG files. Multiple files supported.
              </p>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />

            {uploading && (
              <div className="flex items-center justify-center gap-2 text-blue-700">
                <Settings className="h-4 w-4 animate-spin" />
                <span>Uploading and processing certificates...</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Certificates Display */}
      {totalCerts > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedCertificates).map(([type, certs]) => (
            <Card key={type} className="bg-white border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-slate-900 flex items-center gap-2">
                  <span className="text-2xl">{getCertificateTypeIcon(type)}</span>
                  {type.replace('_', ' ').toUpperCase()} ({certs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {certs.map((cert) => (
                    <div key={cert.id} className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="text-slate-900 font-medium mb-1">{cert.certificate_name}</h4>
                          <p className="text-slate-500 text-sm">{cert.issuing_organization}</p>
                        </div>
                        <Badge className={getStatusColor(cert.status)}>
                          {cert.status?.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {cert.expiration_date && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-slate-500" />
                            <span className="text-slate-600">
                              Expires: {formatDate(cert.expiration_date)}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-slate-500" />
                          <span className="text-slate-600">
                            {cert.file_type?.toUpperCase()} file
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-4">
                        <Button
                          onClick={() => window.open(`${backendUrl}${cert.file_url}`, '_blank')}
                          size="sm"
                          variant="outline"
                          className="bg-white border-slate-200 text-slate-900 hover:bg-slate-50"
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          onClick={() => deleteCertificate(cert.id)}
                          size="sm"
                          variant="outline"
                          className="bg-red-600 border-red-600 text-white hover:bg-red-700"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <div className="text-center">
              <Shield className="h-16 w-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-slate-900 font-medium mb-2">No Safety Certificates</h3>
              <p className="text-slate-500 mb-4">
                Upload safety certificates to track expiration dates and maintain compliance.
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload First Certificate
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy Safety Tickets Section */}
      {memberSafetyTickets.length > 0 && (
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-slate-900">Legacy Safety Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {memberSafetyTickets.map((ticket, index) => (
                <div key={index} className="p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-slate-900 font-medium">{ticket.title || 'Safety Issue'}</h4>
                      <p className="text-slate-500 text-sm mt-1">{ticket.description || 'No description'}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-slate-500 text-xs">
                          Created: {formatDate(ticket.created_at)}
                        </span>
                        {ticket.resolved_at && (
                          <span className="text-slate-500 text-xs">
                            Resolved: {formatDate(ticket.resolved_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    <Badge variant={ticket.status === 'open' ? 'destructive' : 'outline'}>
                      {ticket.status || 'unknown'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Safety Overview Component
const SafetyOverview = ({ safetyTickets, teamMembers }) => {
  const openTickets = safetyTickets.filter(ticket => ticket.status === 'open');
  const recentTickets = safetyTickets.slice(0, 10);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 text-sm">Open Safety Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{openTickets.length}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 text-sm">Total Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">{safetyTickets.length}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-slate-800 text-sm">This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-700">
              {safetyTickets.filter(ticket => 
                new Date(ticket.created_at).getMonth() === new Date().getMonth()
              ).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Safety Tickets */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-slate-900">Recent Safety Tickets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentTickets.map((ticket) => (
              <div key={ticket.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 shadow-sm">
                <div>
                  <p className="text-slate-900 font-medium">{ticket.incident_type}</p>
                  <p className="text-slate-500 text-sm">{ticket.description?.substring(0, 60)}...</p>
                </div>
                <Badge variant={ticket.status === 'open' ? 'destructive' : 'outline'}>
                  {ticket.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Pay Rates / Schedule Overview tabs removed — Pay Rates moved to Field Tracker (PM/Estimators only).

export default TeamManagement;